/* 2026-02 · Public /book/{isbn} landing page.
 *
 * Rendered when a reader scans the QR code on the back cover of a
 * Crown & Rose print book. NO AUTH REQUIRED — the API endpoint
 * (/api/isbn/public/{isbn}) is intentionally public and only
 * exposes fields safe for print/scan.
 *
 * Layout is deliberately mobile-first (most scans happen on phone
 * cameras) and Windows-Fluent-consistent with the rest of the app.
 *
 * 2026-02 · SEO — hydrates <title>, description, canonical, and OG
 * tags on client render via `useSeoMeta`. Crawlers that don't run JS
 * get the same values from the FastAPI prerender middleware (see
 * backend/app/seo_prerender_middleware.py) which recognises
 * /book/:isbn as a dynamic route and looks up the record itself.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, BookOpen, ShoppingCart, User, ScanLine } from 'lucide-react';
import { useSeoMeta } from '@/lib/useSeoMeta';
import StructuredData, { bookLd, breadcrumbLd } from '@/components/seo/StructuredData';
import ReaderFontBar from '@/components/panels/ReaderFontBar';
import PublicNavBar from '@/components/landing/PublicNavBar';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookLandingPage() {
  const { isbn } = useParams();
  const [state, setState] = useState({ loading: true });
  const [resolvedCss, setResolvedCss] = useState('');
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/optional`, { withCredentials: true })
      .then((r) => setSignedIn(Boolean(r.data?.authenticated)))
      .catch(() => setSignedIn(false));
  }, []);

  const fallback = useMemo(() => {
    const b = state.data;
    if (!b) return { title: `Book · ISBN ${isbn} · Crown & Rose` };
    const authors = (b.contributors || []).join(', ') || 'Independent author';
    return {
      title: `${b.title || 'Untitled'} by ${authors} — Crown & Rose`,
      description: b.description ||
        `${b.title} by ${authors}. Independent ${b.format || 'book'} published direct-to-reader through Crown & Rose.`,
      keywords: `${b.title} book, ${authors} novel, indie fiction, buy ${b.format || 'book'} direct, ISBN ${b.isbn}`,
      og_image: b.cover_url || '',
      og_type: 'book',
      canonical: typeof window !== 'undefined' ? window.location.href : '',
    };
  }, [state.data, isbn]);
  useSeoMeta(`/book/${isbn}`, fallback);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/isbn/public/${encodeURIComponent(isbn)}`);
        if (alive) {
          setState({ loading: false, data: r.data });
          // Inject Microsoft Clarity if the author has configured it.
          // Guard: only accept the real Clarity ID format (alphanumeric / _ / -,
          // 1-30 chars) — prevents any stored-XSS payload from executing.
          const cid = r.data?.clarity_project_id;
          const _CLARITY_RE = /^[a-zA-Z0-9_-]{1,30}$/;
          if (cid && _CLARITY_RE.test(cid) && !document.getElementById('ms-clarity-script')) {
            const s = document.createElement('script');
            s.id = 'ms-clarity-script';
            s.type = 'text/javascript';
            // Use s.text (not innerHTML) so HTML-entity parsing cannot be abused.
            s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${cid}");`;
            document.head.appendChild(s);
          }
        }
      } catch (e) {
        if (alive) setState({ loading: false, error: e?.response?.data?.detail || 'Book not found for this ISBN.' });
      }
    })();
    return () => { alive = false; };
  }, [isbn]);

  if (state.loading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground"
             data-testid="book-landing-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Looking up ISBN {isbn}…
        </div>
      </Shell>
    );
  }

  if (state.error) {
    return (
      <Shell>
        <div className="mx-auto max-w-md rounded-md border border-border bg-card p-6 text-center"
             data-testid="book-landing-error">
          <AlertCircle className="mx-auto h-8 w-8 text-amber-500" />
          <h1 className="mt-3 text-lg font-semibold">We couldn&rsquo;t find that ISBN</h1>
          <p className="mt-2 text-[13px] text-muted-foreground">{state.error}</p>
          <p className="mt-2 text-[11px] text-muted-foreground font-mono">ISBN {isbn}</p>
          <Link to="/bookstore" className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[12.5px] font-semibold text-primary-foreground hover:opacity-90"
                data-testid="book-landing-error-store-link">
            <BookOpen className="h-3.5 w-3.5" /> Browse the Book Store
          </Link>
        </div>
      </Shell>
    );
  }

  const b = state.data;
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : '';
  return (
    <Shell>
      <StructuredData
        id="book"
        data={bookLd({
          siteUrl,
          isbn,
          title: b.title,
          authors: b.contributors || [],
          description: b.description,
          coverUrl: b.cover_url,
          language: b.language,
          format: b.format,
          retailPrice: b.retail_price,
          currency: b.currency,
        })}
      />
      <StructuredData
        id="breadcrumb"
        data={breadcrumbLd([
          { name: 'Crown & Rose', url: siteUrl },
          { name: 'Book Store', url: `${siteUrl}/bookstore` },
          { name: b.title || 'Book', url: `${siteUrl}/book/${isbn}` },
        ])}
      />
      <article className="mx-auto max-w-3xl overflow-hidden rounded-lg border border-border bg-card shadow-sm"
               data-testid="book-landing">
        <div className="grid grid-cols-1 gap-0 md:grid-cols-[220px_1fr]">
          <div className="bg-muted/40 p-4">
            {b.cover_url ? (
              <img src={b.cover_url} alt={`${b.title} cover`}
                   data-testid="book-landing-cover"
                   className="mx-auto aspect-[2/3] w-full max-w-[200px] rounded-md object-cover shadow-md" />
            ) : (
              <div className="mx-auto flex aspect-[2/3] w-full max-w-[200px] items-center justify-center rounded-md border border-dashed border-border bg-background text-muted-foreground"
                   data-testid="book-landing-cover-placeholder">
                <BookOpen className="h-8 w-8 opacity-40" />
              </div>
            )}
          </div>
          <div className="p-5">
            <div className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-primary">
              {b.publisher}
            </div>
            <h1 className="mt-1 text-2xl font-semibold leading-tight" data-testid="book-landing-title">
              {b.title || 'Untitled'}
            </h1>
            {b.subtitle && (
              <p className="mt-1 text-[13.5px] text-muted-foreground" data-testid="book-landing-subtitle">
                {b.subtitle}
              </p>
            )}
            {b.contributors && b.contributors.length > 0 && (
              <p className="mt-2 text-[13px]" data-testid="book-landing-contributors">
                by <span className="font-semibold">{b.contributors.join(', ')}</span>
              </p>
            )}

            <ul className="mt-3 flex flex-wrap gap-1.5 text-[10.5px] text-muted-foreground">
              {b.format && <Badge>{b.format}</Badge>}
              {b.edition && <Badge>Edition {b.edition}</Badge>}
              {b.publication_date && <Badge>{b.publication_date}</Badge>}
              {b.language && <Badge>{b.language}</Badge>}
              {b.retail_price_display && <Badge>{b.retail_price_display}</Badge>}
            </ul>

            {b.description && (
              <p className="mt-4 whitespace-pre-line text-[13px] leading-relaxed text-foreground/90"
                 data-testid="book-landing-description">
                {b.description}
              </p>
            )}

            {b.approvals && b.approvals.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
                   data-testid="book-landing-approvals">
                {b.approvals.map((a) => (
                  <span key={a.id || a.reviewer_kind}
                        className="rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
                    {a.reviewer_kind} Approved · {(a.decided_at || '').slice(0, 10)}
                  </span>
                ))}
              </div>
            )}

            {b.sample_text && (
              <>
                {(resolvedCss || b.publishing_css) && (
                  <style dangerouslySetInnerHTML={{ __html: resolvedCss || b.publishing_css }} />
                )}
                <ReaderFontBar defaultCss={b.publishing_css || ''}
                                signedIn={signedIn}
                                onCssChange={setResolvedCss} />
                <div className="mt-4 pub-body rounded-md border border-border bg-panel-header/20 p-4"
                     data-testid="book-landing-sample-body">
                  <div className="mb-2 text-[9px] uppercase tracking-[0.24em] text-primary">
                    Read a passage
                  </div>
                  <div dangerouslySetInnerHTML={{
                    __html: `<section class="pub-chapter"><h2 class="pub-chapter-title">Chapter 1</h2>${
                      b.sample_text.split(/\n{2,}/).map((p, i) => `<p class="pub-para${i===0?' first':''}">${p.replace(/[&<>]/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]))}</p>`).join('')
                    }</section>`,
                  }} />
                </div>
              </>
            )}

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {b.bookstore_url && (
                <Link to={b.bookstore_url} data-testid="book-landing-buy-store"
                      className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-[12.5px] font-semibold text-primary-foreground hover:opacity-90">
                  <ShoppingCart className="h-3.5 w-3.5" /> View in the Book Store
                </Link>
              )}
              {b.buy_url && (
                <a href={b.buy_url} target="_blank" rel="noreferrer"
                   data-testid="book-landing-buy-external"
                   className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-[12.5px] font-semibold hover:bg-secondary">
                  <ShoppingCart className="h-3.5 w-3.5" /> Buy this book
                </a>
              )}
            </div>
          </div>
        </div>

        {b.author && (
          <aside className="border-t border-border bg-muted/30 p-5" data-testid="book-landing-author-card">
            <div className="flex items-start gap-3">
              {b.author.avatar_url ? (
                <img src={b.author.avatar_url} alt=""
                     className="h-14 w-14 rounded-full object-cover" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background text-muted-foreground">
                  <User className="h-6 w-6 opacity-60" />
                </div>
              )}
              <div className="flex-1">
                <div className="text-[10.5px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">About the author</div>
                <div className="mt-0.5 text-[15px] font-semibold" data-testid="book-landing-author-name">
                  {b.author.name}
                </div>
                {b.author.bio && (
                  <p className="mt-1 line-clamp-6 text-[12.5px] leading-relaxed text-foreground/90"
                     data-testid="book-landing-author-bio">
                    {b.author.bio}
                  </p>
                )}
                {b.author.slug && (
                  <Link to={`/authors/${b.author.slug}`} data-testid="book-landing-author-more"
                        className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-primary hover:underline">
                    More from {b.author.name} →
                  </Link>
                )}
              </div>
            </div>
          </aside>
        )}

        <footer className="border-t border-border bg-card px-5 py-3 text-[10.5px] text-muted-foreground"
                data-testid="book-landing-isbn-footer">
          <div className="flex flex-wrap items-center gap-2">
            <ScanLine className="h-3 w-3" />
            <span>About this ISBN · <span className="font-mono">{b.isbn}</span></span>
            <span className="opacity-60">·</span>
            <span>Published by {b.publisher}</span>
            {b.bisac_codes && b.bisac_codes.length > 0 && (
              <>
                <span className="opacity-60">·</span>
                <span className="font-mono">{b.bisac_codes.join(', ')}</span>
              </>
            )}
          </div>
        </footer>
      </article>
    </Shell>
  );
}

function Badge({ children }) {
  return (
    <span className="rounded-full border border-border bg-background px-2 py-[1px] uppercase tracking-wider">
      {children}
    </span>
  );
}

function Shell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <PublicNavBar
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login?mode=signup')}
      />
      <main className="px-4 py-6">{children}</main>
    </div>
  );
}
